import {
  LAB_PREVIEW_SCENE_GROUPS,
  LAB_PREVIEW_SCENES,
  labPreviewScenesOf,
  type LabPreviewSceneId,
} from './labScenes';

export interface LabSceneSelectProps {
  value: LabPreviewSceneId;
  onChange(next: LabPreviewSceneId): void;
  /** Loads the preview into the same state again (a ceremony to watch once more); no button without it. */
  onReplay?: (() => void) | undefined;
}

const STEP_CLASS =
  'flex h-7 w-6 items-center justify-center rounded border border-neutral-700 bg-neutral-900 font-mono text-[12px] text-neutral-300 outline-none transition-colors hover:bg-neutral-800 hover:text-white focus-visible:ring-1 focus-visible:ring-white/60 disabled:cursor-not-allowed disabled:opacity-40';

/**
 * The custom pack lab's state in the preview: today's live lab, or one of the states the iframe seeds (a full pack, the
 * odds step, a reveal…), grouped by step. The arrows step through them in order, to walk every state of a theme;
 * Replay loads the one on screen again.
 */
export function LabSceneSelect({ value, onChange, onReplay }: LabSceneSelectProps) {
  const index = LAB_PREVIEW_SCENES.findIndex((scene) => scene.id === value);
  const step = (by: number) => {
    const next = LAB_PREVIEW_SCENES[index + by];
    if (next) onChange(next.id);
  };

  return (
    <div className="flex items-center gap-1.5">
      <label htmlFor="lab-scene-select" className="font-mono text-[10px] uppercase tracking-wide text-neutral-400">
        Lab state
      </label>
      <button
        type="button"
        aria-label="Previous lab state"
        className={STEP_CLASS}
        disabled={index <= 0}
        onClick={() => step(-1)}
      >
        ‹
      </button>
      <select
        id="lab-scene-select"
        value={value}
        onChange={(e) => onChange(e.target.value as LabPreviewSceneId)}
        className="h-7 max-w-[220px] rounded border border-neutral-700 bg-neutral-900 px-2 font-mono text-[11px] text-white outline-none hover:bg-neutral-800 focus-visible:ring-1 focus-visible:ring-white/60"
      >
        {LAB_PREVIEW_SCENE_GROUPS.map((group) => (
          <optgroup key={group} label={group}>
            {labPreviewScenesOf(group).map((scene) => (
              <option key={scene.id} value={scene.id}>
                {scene.label}
              </option>
            ))}
          </optgroup>
        ))}
      </select>
      <button
        type="button"
        aria-label="Next lab state"
        className={STEP_CLASS}
        disabled={index < 0 || index >= LAB_PREVIEW_SCENES.length - 1}
        onClick={() => step(1)}
      >
        ›
      </button>
      {onReplay && (
        <button
          type="button"
          title="Load this state again"
          className="flex h-7 items-center gap-1 rounded border border-neutral-700 bg-neutral-900 px-2 font-mono text-[11px] text-neutral-300 outline-none transition-colors hover:bg-neutral-800 hover:text-white focus-visible:ring-1 focus-visible:ring-white/60"
          onClick={onReplay}
        >
          <span aria-hidden>↻</span>
          Replay
        </button>
      )}
    </div>
  );
}
